import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { GitMerge, AlertCircle, Copy, FileWarning, Clock, ArrowRight, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState, Badge, Btn, PageHeader, Panel } from "@/components/common";
import Modal from "@/components/Modal";

export default function ReviewQueue() {
  const { workspaceId, canEdit } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("duplicates");
  const [mergePair, setMergePair] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    const { data } = await api.get(`/review?workspace_id=${workspaceId}`);
    setData(data);
  }, [workspaceId]);

  useEffect(() => { setData(null); load(); }, [load]);

  if (!data) return <div className="p-8"><Loading /></div>;

  const TABS = [
    { k: "duplicates", l: "Possible duplicates", icon: Copy, n: data.duplicates.length },
    { k: "candidates", l: "New candidates", icon: AlertCircle, n: data.candidates.length },
    { k: "incomplete", l: "Incomplete identities", icon: FileWarning, n: data.incomplete.length },
    { k: "conflicting_evidence", l: "Conflicting observations", icon: FileWarning, n: data.conflicting_evidence.length },
    { k: "stale", l: "Stale connections", icon: Clock, n: data.stale.length },
  ];

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <PageHeader title="Review Queue" sub="New candidates, incomplete identities, possible duplicates, conflicting observations, and stale connections." />

      <div className="flex gap-1 mb-6 flex-wrap" style={{ borderBottom: "1px solid var(--si-border)" }}>
        {TABS.map((t) => (
          <button key={t.k} data-testid={`review-tab-${t.k}`} onClick={() => setTab(t.k)} className="flex items-center gap-1.5 px-4 py-2 text-sm transition-colors duration-200"
            style={{ color: tab === t.k ? "var(--si-text)" : "var(--si-text-2)", borderBottom: tab === t.k ? "2px solid var(--si-teal-light)" : "2px solid transparent" }}>
            <t.icon size={14} strokeWidth={1.5} /> {t.l} <span className="font-mono text-xs" style={{ color: t.n > 0 ? "var(--si-warning)" : "var(--si-text-2)" }}>{t.n}</span>
          </button>
        ))}
      </div>

      {tab === "duplicates" && (
        data.duplicates.length === 0 ? <EmptyState icon={Copy} title="No possible duplicates" hint="Entities with the same name and type would appear here." /> : (
          <div className="space-y-4">
            {data.duplicates.map((grp, i) => (
              <Panel key={i} className="p-4">
                <div className="si-label mb-3">{grp.type} · "{grp.name}" · {grp.entities.length} records</div>
                <div className="grid md:grid-cols-2 gap-3">
                  {grp.entities.map((e) => (
                    <div key={e.id} className="p-3 text-sm" style={{ border: "1px solid var(--si-teal)", borderRadius: 2 }}>
                      <Link to={`/app/entities/${e.id}`} style={{ color: "var(--si-text)" }}>{e.name}</Link>
                      <div className="font-mono text-xs mt-1" style={{ color: "var(--si-text-2)" }}>id {e.id.slice(0, 8)} · <Badge value={e.verification_status} /></div>
                      <div className="text-xs mt-1" style={{ color: "var(--si-text-2)" }}>{e.description || "no description"}</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--si-text-2)" }}>{e.address || "no address"}</div>
                    </div>
                  ))}
                </div>
                {canEdit && grp.entities.length >= 2 && (
                  <div className="mt-3"><Btn variant="secondary" data-testid={`merge-${i}`} onClick={() => setMergePair(grp.entities)}><GitMerge size={14} /> Review & merge</Btn></div>
                )}
              </Panel>
            ))}
          </div>
        )
      )}

      {["candidates", "incomplete", "stale"].includes(tab) && (
        data[tab].length === 0 ? <EmptyState icon={AlertCircle} title="Nothing to review here" /> : (
          <Panel>
            {data[tab].map((e) => (
              <div key={e.id} className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
                <div>
                  <Link to={`/app/entities/${e.id}`} className="text-sm" style={{ color: "var(--si-text)" }}>{e.name}</Link>
                  <div className="si-label mt-0.5">{e.type} · updated {new Date(e.updated_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-2"><Badge value={e.verification_status} /><Link to={`/app/entities/${e.id}`}><Btn variant="ghost" className="!py-1 !px-2 text-xs">Resolve <ArrowRight size={13} /></Btn></Link></div>
              </div>
            ))}
          </Panel>
        )
      )}

      {tab === "conflicting_evidence" && (
        data.conflicting_evidence.length === 0 ? <EmptyState icon={FileWarning} title="No conflicting observations" /> : (
          <Panel>
            {data.conflicting_evidence.map((ev) => (
              <div key={ev.id} className="px-5 py-3" style={{ borderBottom: "1px solid var(--si-border)" }}>
                <div className="flex items-center gap-2"><Badge value="disputed" /><span className="si-label">{ev.classification}</span></div>
                <div className="text-sm mt-1" style={{ color: "var(--si-text)" }}>{ev.excerpt}</div>
                {ev.entity_id && <Link to={`/app/entities/${ev.entity_id}`} className="text-xs" style={{ color: "var(--si-silver)" }}>Open entity →</Link>}
              </div>
            ))}
          </Panel>
        )
      )}

      {mergePair && <MergeModal workspaceId={workspaceId} pair={mergePair} onClose={() => setMergePair(null)} onMerged={() => { setMergePair(null); load(); }} />}
    </div>
  );
}

function MergeModal({ workspaceId, pair, onClose, onMerged }) {
  const [a, b] = pair;
  const [canonical, setCanonical] = useState(b.id);
  const [busy, setBusy] = useState(false);
  const FIELDS = ["type", "verification_status", "relationship_stage", "description", "address", "notes"];

  const merge = async () => {
    const target = canonical;
    const source = canonical === a.id ? b.id : a.id;
    setBusy(true);
    try {
      await api.post(`/entities/merge?workspace_id=${workspaceId}`, { source_id: source, target_id: target });
      toast.success("Merged — memberships, relationships, evidence, and tasks preserved");
      onMerged();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cell = (e, field) => {
    const other = e.id === a.id ? b : a;
    const conflict = (e[field] || "") !== (other[field] || "");
    return <td className="px-3 py-2 text-sm" style={{ color: conflict ? "var(--si-warning)" : "var(--si-text)", borderBottom: "1px solid var(--si-border)" }}>{String(e[field] ?? "—") || "—"}</td>;
  };

  return (
    <Modal open onClose={onClose} title="Review & merge duplicates" width={720} testid="merge-modal">
      <div className="text-sm mb-4" style={{ color: "var(--si-text-2)" }}>Conflicting fields are highlighted. Choose which record survives as canonical. Memberships, relationships, evidence, tasks, and history are preserved; former IDs are recorded.</div>
      <table className="w-full">
        <thead>
          <tr>
            <th className="si-label text-left px-3 py-2 font-normal">Field</th>
            {[a, b].map((e) => (
              <th key={e.id} className="text-left px-3 py-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--si-text)" }}>
                  <input type="radio" name="canonical" checked={canonical === e.id} onChange={() => setCanonical(e.id)} data-testid={`canonical-${e.id}`} />
                  {canonical === e.id ? <span style={{ color: "var(--si-teal-light)" }}>★ canonical</span> : "keep"}
                </label>
                <div className="font-mono text-xs mt-1" style={{ color: "var(--si-text-2)" }}>{e.id.slice(0, 8)}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr><td className="si-label px-3 py-2">name</td><td className="px-3 py-2 text-sm" style={{ color: "var(--si-text)" }}>{a.name}</td><td className="px-3 py-2 text-sm" style={{ color: "var(--si-text)" }}>{b.name}</td></tr>
          {FIELDS.map((f) => (<tr key={f}><td className="si-label px-3 py-2">{f.replace(/_/g, " ")}</td>{cell(a, f)}{cell(b, f)}</tr>))}
        </tbody>
      </table>
      <div className="flex justify-end mt-5"><Btn variant="primary" onClick={merge} disabled={busy} data-testid="confirm-merge"><Check size={15} /> {busy ? "Merging…" : "Merge records"}</Btn></div>
    </Modal>
  );
}
